import React, {CSSProperties, ReactNode, useState} from "react";
import {Button, Stack, Toolbar, Typography} from "@mui/material";
import {Add, Delete, DoubleArrow, Edit, ExpandMore, Square} from "@mui/icons-material";
import {ProcessButton} from "../input/ProcessButton";
import {CodeBlock} from "../CodeBlock";
import ReactDiffViewer, {DiffMethod} from "react-diff-viewer-continued";
import {GlobalState} from "../../../global/GlobalState";
import SyntaxHighlighter from "react-syntax-highlighter";
import {vs, vs2015} from "react-syntax-highlighter/dist/esm/styles/hljs";
import {LazyAccordion} from "../containers/LazyAccordion";
import {translateFunction} from "../../../localization/TranslationService";
import {json} from "@codemirror/lang-json";

const translate = translateFunction();

export interface DeployDisplayProps<TItem> {
	readonly name: string;
	readonly stages: string[];
	readonly items: (TItem | null)[];
	readonly keyMapper: (item: TItem) => string;
	readonly labelMapper?: (item: TItem) => string;
	readonly putItem?: (stage: string, item: TItem) => Promise<any>;
	readonly deleteItem?: (stage: string, itemId: string) => Promise<any>;
	readonly openItemDetails?: (id: string) => void;
	readonly openNewItemDetails?: (id: string) => void;
	readonly allowBackwardsSync?: boolean;
}

export function DeployDisplay<TItem>(
	{
		name,
		stages,
		items,
		keyMapper,
		labelMapper,
		putItem,
		deleteItem,
		openItemDetails,
		openNewItemDetails,
		allowBackwardsSync,
	}: DeployDisplayProps<TItem>
) {
	const [stateItems, setStateItems] = useState(items);
	
	function update(stage: string, setup: TItem | null) {
		const index = stages.indexOf(stage);
		const newSetups = [...stateItems];
		newSetups[index] = setup;
		setStateItems(newSetups);
	}
	
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	const id: string = stateItems.map(it => it ? keyMapper(it) : undefined).filter(it => !!it)[0]!;
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	const label: string = labelMapper ? stateItems.map(it => it ? labelMapper(it) : undefined).filter(it => !!it)[0]! : id;
	
	const status = [
		computeStatus(stateItems[0]),
		computeStatus(stateItems[0], stateItems[1]),
		computeStatus(stateItems[1], stateItems[2]),
	];
	
	const containerStyle = {
		flex: 1,
		border: "none",
		boxShadow: "none",
		borderRadius: "unset",
	};
	
	return <LazyAccordion
		containerProps={{key: id, style: containerStyle}}
		summaryProps={{expandIcon: <ExpandMore/>}}
		summary={<Typography display={"flex"}>
			{status.map((it, index) => <Square key={index} color={it} fontSize={"small"}/>)}
			{name}: {label}
		</Typography>}>
		<DeploySet<TItem>
			id={id}
			stages={stages}
			items={stateItems}
			update={update}
			putItem={putItem}
			deleteItem={deleteItem}
			openItemDetails={openItemDetails}
			openNewItemDetails={openNewItemDetails}
			allowBackwardsSync={allowBackwardsSync}
		/>
	</LazyAccordion>;
}

function computeStatus(left: any | null, right?: any | null): "disabled" | "success" | "info" | "warning" {
	if (right === undefined) {
		return left === undefined || left === null ? "disabled" : "success";
	}
	
	if (right === null) {
		return "disabled";
	}
	
	if (left === null) {
		return "warning";
	}
	
	const oldValue = jsonFormat(right);
	const newValue = jsonFormat(left);
	const isDifferent = oldValue !== newValue;
	
	return isDifferent ? "info" : "success";
}

interface DeploySetProps<TItem> {
	readonly id: string;
	readonly stages: string[];
	readonly items: (TItem | null)[];
	readonly update: (stage: string, item: TItem | null) => void;
	readonly putItem?: (stage: string, item: TItem) => Promise<any>;
	readonly deleteItem?: (stage: string, itemId: string) => Promise<any>;
	readonly openItemDetails?: (id: string) => void;
	readonly openNewItemDetails?: (id: string) => void;
	readonly allowBackwardsSync?: boolean;
}

function DeploySet<TItem>(
	{
		id,
		stages,
		items,
		putItem,
		update,
		deleteItem,
		openItemDetails,
		openNewItemDetails,
		allowBackwardsSync,
	}: DeploySetProps<TItem>
) {
	let previousItem: TItem | null = null;
	return <Stack direction={"row"} maxHeight={"80vh"}>
		{items.map((item, index) => {
			const previous = previousItem;
			previousItem = item;
			return <Stage
				key={`stage-${index}`}
				stage={stages[index]}
				item={item}
				previous={previous}
				next={items[index + 1] ?? null}
				id={id}
				index={index}
				update={update}
				putItem={putItem}
				deleteItem={deleteItem}
				openItemDetails={openItemDetails}
				openNewItemDetails={openNewItemDetails}
				allowBackwardsSync={allowBackwardsSync}
			/>;
		})}
	</Stack>;
}

interface StageProps<TItem> {
	readonly id: string;
	readonly index: number;
	readonly stage: string;
	readonly item: TItem | null;
	readonly previous: TItem | null;
	readonly next: TItem | null;
	readonly update: (stage: string, item: TItem | null) => void;
	readonly putItem?: (stage: string, item: TItem) => Promise<any>;
	readonly deleteItem?: (stage: string, itemId: string) => Promise<any>;
	readonly openItemDetails?: (id: string) => void;
	readonly openNewItemDetails?: (id: string) => void;
	readonly allowBackwardsSync?: boolean;
}

function Stage<TItem>(
	{
		id,
		index,
		stage,
		item,
		previous,
		next,
		update,
		putItem,
		deleteItem,
		openItemDetails,
		openNewItemDetails,
		allowBackwardsSync,
	}: StageProps<TItem>
) {
	const header = <Toolbar>
		<Stack direction={"row"} gap={1}>
			<Typography variant={"h6"}>{translate(stage)}</Typography>
			
			{!item && index === 0 && openNewItemDetails && <Button
				key="createNew"
				startIcon={<Add/>}
				onClick={() => openNewItemDetails(id)}
			>{translate("Create")}</Button>}
			
			{!item && previous && putItem && <ProcessButton
				key="createForwards"
				defaultText={translate("Create")}
				completedText={translate("Created")}
				inProgressText={translate("Creating")}
				startIcon={<DoubleArrow/>}
				onClick={() => createItemFunction(stage, previous, update, putItem)}
			/>}
			
			{!item && next && putItem && allowBackwardsSync && <Button
				key="createBackwards"
				startIcon={<DoubleArrow style={{transform: "scaleX(-1)"}}/>}
				onClick={() => createItemFunction(stage, next, update, putItem)}
			>{translate("Create")}</Button>}
			
			{item && !previous && index > 0 && deleteItem && <ProcessButton
				key="deleteItem"
				defaultText={translate("Delete")}
				completedText={translate("Deleted")}
				inProgressText={translate("Deleting")}
				startIcon={<Delete/>}
				onClick={() => deleteItemFunction(stage, id, update, deleteItem)}
			/>}
			
			{item && previous && putItem && Render(() => {
				const isDifferent = jsonFormat(item) !== jsonFormat(previous);
				
				if (isDifferent)
					return <ProcessButton
						key="syncForwards"
						defaultText={translate("Apply Changes")}
						completedText={translate("Applied")}
						inProgressText={translate("Applying")}
						startIcon={<DoubleArrow/>}
						onClick={() => applyChangesFunction(stage, previous, update, putItem)}
					/>;
			})}
			
			{item && next && putItem && Render(() => {
				const isDifferent = jsonFormat(item) !== jsonFormat(next);
				
				if (isDifferent)
					return <ProcessButton
						key="syncBackwards"
						defaultText={translate("Apply Changes")}
						completedText={translate("Applied")}
						inProgressText={translate("Applying")}
						startIcon={<DoubleArrow style={{transform: "scaleX(-1)"}}/>}
						onClick={() => applyChangesFunction(stage, next, update, putItem)}
					/>;
			})}
			
			{item && index === 0 && openItemDetails && <Button
				key="openItem"
				startIcon={<Edit/>}
				onClick={() => openItemDetails(id)}
			>{translate("Edit")}</Button>}
		</Stack>
	</Toolbar>;
	
	const content = !item
		? <div
			style={{
				textAlign: "center",
				margin: "auto",
			}}>{translate("not found")}</div>
		: !previous
			? <div style={{overflow: "auto"}}>
				<CodeBlock
					name={stage}
					syntax={json()}
					showHeader={false}
					removePadding
					// customStyle={{
					// 	padding: "0",
					// 	margin: "0",
					// 	overflowX: "unset",
					// }}
				>{jsonFormat(item)}</CodeBlock>
			</div>
			: <div style={{overflow: "auto"}}>
				<ReactDiffViewer
					styles={defaultStyles}
					oldValue={jsonFormat(item)}
					newValue={jsonFormat(previous)}
					splitView={false}
					useDarkTheme={GlobalState.paletteMode === "dark"}
					compareMethod={DiffMethod.LINES}
					renderContent={source => {
						return <SyntaxHighlighter
							language={"json"}
							style={GlobalState.paletteMode === "dark" ? vs2015 : vs}
							CodeTag={"span"}
							PreTag={"span"}
							customStyle={{
								padding: "0",
								lineHeight: "initial",
							}}
						>
							{source}
						</SyntaxHighlighter>;
					}}
				/>
			</div>;
	
	return <Row index={`${id}:${index}`} className={"diff-viewer"}>
		{header}
		{content}
	</Row>;
}

function Render(body: () => ReactNode | undefined) {
	return body();
}

function Row(props: {
	index: string;
	children: ReactNode;
	className?: string;
	style?: CSSProperties;
}) {
	return <Stack
		key={props.index}
		className={props.className}
		style={{...props.style, flex: "1 1 1px", display: "flex", minWidth: 0}}
	>
		{props.children}
	</Stack>;
}

function jsonFormat(value: any): string {
	return JSON.stringify(value, null, 4);
}

async function applyChangesFunction<TItem>(
	stage: string,
	item: TItem,
	updateItem: (stage: string, item: TItem) => void,
	putItem: (stage: string, item: TItem) => Promise<any>,
) {
	await putItem(stage, item);
	updateItem(stage, item);
}

async function createItemFunction<TItem>(
	stage: string,
	item: TItem,
	updateItem: (stage: string, setup: TItem) => void,
	putItem: (stage: string, item: TItem) => Promise<any>,
) {
	await putItem(stage, item);
	updateItem(stage, item);
}

async function deleteItemFunction(
	stage: string,
	itemId: string,
	updateSetup: (stage: string, setup: null) => void,
	deleteItem: (stage: string, itemId: string) => Promise<any>,
) {
	deleteItem(stage, itemId);
	updateSetup(stage, null);
}

const defaultStyles = {
	variables: {
		light: {
			diffViewerBackground: "#ffffff",
			diffViewerColor: "#212529",
			addedBackground: "#cdffd8",
			addedColor: "#24292e",
			removedBackground: "#ffdce0",
			removedColor: "#24292e",
			wordAddedBackground: "#acf2bd",
			wordRemovedBackground: "#fdb8c0",
			addedGutterBackground: "#cdffd8",
			removedGutterBackground: "#ffdce0",
			gutterBackground: "#ffffff",
			gutterBackgroundDark: "#f3f1f1",
			highlightBackground: "#fffbdd",
			highlightGutterBackground: "#fff5b1",
			codeFoldGutterBackground: "#f1f8ff",
			codeFoldBackground: "#f1f8ff",
			emptyLineBackground: "#fafbfc",
			gutterColor: "#212529",
			addedGutterColor: "#212529",
			removedGutterColor: "#212529",
			codeFoldContentColor: "#212529",
			diffViewerTitleBackground: "#fafbfc",
			diffViewerTitleColor: "#212529",
			diffViewerTitleBorderColor: "#eee",
		},
		dark: {
			diffViewerBackground: "#1E1E1E",
			diffViewerColor: "#dcdcdc",
			addedBackground: "#447152",
			addedColor: "#dcdcdc",
			removedBackground: "#632b30",
			removedColor: "#dcdcdc",
			wordAddedBackground: "#577f63",
			wordRemovedBackground: "#7d383f",
			addedGutterBackground: "#447152",
			removedGutterBackground: "#632b30",
			gutterBackground: "#1E1E1E",
			gutterBackgroundDark: "#262933",
			highlightBackground: "#2a3967",
			highlightGutterBackground: "#2d4077",
			codeFoldGutterBackground: "#222222",
			codeFoldBackground: "#222222",
			emptyLineBackground: "#363946",
			gutterColor: "#dcdcdc",
			addedGutterColor: "#8c8c8c",
			removedGutterColor: "#8c8c8c",
			codeFoldContentColor: "#dcdcdc",
			diffViewerTitleBackground: "#2f323e",
			diffViewerTitleColor: "#555a7b",
			diffViewerTitleBorderColor: "#353846",
		}
	},
	diffContainer: {}, // style object
	diffRemoved: {}, // style object
	diffAdded: {}, // style object
	marker: {}, // style object
	emptyGutter: {}, // style object
	highlightedLine: {}, // style object
	lineNumber: {}, // style object
	highlightedGutter: {}, // style object
	contentText: {}, // style object
	gutter: {}, // style object
	line: {}, // style object
	wordDiff: {}, // style object
	wordAdded: {}, // style object
	wordRemoved: {}, // style object
	codeFoldGutter: {}, // style object
	codeFold: {}, // style object
	emptyLine: {}, // style object
	content: {}, // style object
	titleBlock: {}, // style object
	splitView: {}, // style object
};
